import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import logo from "@/assets/brand/note-console-logo-transparent.png";

const INTRO_KEY = "note-console-intro-played";

export default function IntroReveal({ onFinish }) {
  const [visible, setVisible] = useState(false);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const alreadyPlayed = window.sessionStorage.getItem(INTRO_KEY);
    if (alreadyPlayed) {
      onFinish?.();
      return;
    }

    setVisible(true);
    const flashTimer = window.setTimeout(() => setFlash(true), 2700);
    const finishTimer = window.setTimeout(() => {
      window.sessionStorage.setItem(INTRO_KEY, "true");
      setVisible(false);
      onFinish?.();
    }, 3300);

    return () => {
      window.clearTimeout(flashTimer);
      window.clearTimeout(finishTimer);
    };
  }, [onFinish]);

  const skipIntro = () => {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(INTRO_KEY, "true");
    }
    setFlash(true);
    window.setTimeout(() => {
      setVisible(false);
      onFinish?.();
    }, 220);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-0 z-[100] overflow-hidden bg-[#FBF8F1]"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, filter: "blur(12px)" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_42%,rgba(79,103,193,0.18),transparent_26%),radial-gradient(circle_at_50%_58%,rgba(213,173,118,0.18),transparent_28%),linear-gradient(135deg,#FCFAF6_0%,#F7F0E6_52%,#F3FBF8_100%)]" />
          <div className="absolute inset-0 opacity-[0.12] [background-image:linear-gradient(rgba(7,60,47,0.10)_1px,transparent_1px),linear-gradient(90deg,rgba(7,60,47,0.08)_1px,transparent_1px)] [background-size:44px_44px]" />

          <div className="relative z-10 flex min-h-screen items-center justify-center px-5">
            <motion.div
              className="relative flex w-full max-w-3xl flex-col items-center"
              initial={{ opacity: 0, y: 28, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
            >
              <motion.div
                className="absolute top-[42%] h-72 w-72 -translate-y-1/2 rounded-full border border-[#D5AD76]/35 md:h-96 md:w-96"
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: [0, 0.9, 0.35], scale: [0.7, 1.08, 1.18] }}
                transition={{ duration: 2.2, ease: "easeOut" }}
              />
              <motion.img
                src={logo}
                alt="Note Console logo"
                className="relative z-10 w-[min(86vw,520px)] object-contain"
                initial={{ opacity: 0, filter: "blur(16px)", y: 18 }}
                animate={{ opacity: 1, filter: "blur(0px)", y: 0 }}
                transition={{ delay: 0.25, duration: 0.9, ease: "easeOut" }}
              />
              <motion.div
                className="mt-6 h-px w-52 bg-gradient-to-r from-transparent via-[#4B67C1] to-transparent"
                initial={{ scaleX: 0, opacity: 0 }}
                animate={{ scaleX: 1, opacity: 1 }}
                transition={{ delay: 1.05, duration: 0.7, ease: "easeOut" }}
              />
              <motion.p
                className="mt-5 max-w-xl text-center font-body text-base font-bold leading-7 text-[#0B1A33]/70 md:text-lg"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.45 }}
              >
                NoteConsole - Plan your days. Capture your thoughts. Track your life.
              </motion.p>
            </motion.div>
          </div>

          <AnimatePresence>
            {flash && (
              <motion.div
                className="absolute inset-0 z-20 bg-white"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0.92, 0.2] }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.48, ease: "easeOut" }}
              />
            )}
          </AnimatePresence>

          <button
            type="button"
            onClick={skipIntro}
            className="absolute bottom-6 right-6 z-30 rounded-full border border-[#D5AD76]/70 bg-white/70 px-4 py-2 font-body text-xs font-bold uppercase tracking-[0.18em] text-[#073C2F] shadow-sm backdrop-blur transition hover:bg-white"
          >
            Skip Intro
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
